import type { ReactNode } from 'react';
import './components.css';

export interface SegmentedOption<T extends string> { value: T; label: ReactNode }
export function Segmented<T extends string>({ value, onChange, options, block, ariaLabel }: { value: T; onChange: (v: T) => void; options: SegmentedOption<T>[]; block?: boolean; ariaLabel?: string }) {
  return (
    <div className={`k-seg ${block ? 'k-seg--block' : ''}`} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} className={`k-seg__opt ${o.value === value ? 'k-seg__opt--active' : ''}`} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export function CheckChip({ on, onToggle, dot, children }: { on: boolean; onToggle: () => void; dot?: string; children: ReactNode }) {
  return (
    <button type="button" className={`k-checkchip ${on ? 'k-checkchip--on' : ''}`} aria-pressed={on} onClick={onToggle}>
      {dot && <span className="k-checkchip__dot" style={{ background: dot }} />}
      {children}
      {on && <span aria-hidden>✓</span>}
    </button>
  );
}

export function TokenList({ items, onRemove, onAdd, addLabel, placeholder, tone = 'neutral' }: { items: string[]; onRemove: (i: number) => void; onAdd: (v: string) => void; addLabel: ReactNode; placeholder?: string; tone?: 'neutral' | 'danger' }) {
  return (
    <div className="k-tokenlist">
      {items.map((it, i) => (
        <div key={i} className={`k-token ${tone === 'danger' ? 'k-token--danger' : ''}`}>
          <span>{it}</span>
          <button type="button" className="k-token__remove" aria-label="remove" onClick={() => onRemove(i)}>×</button>
        </div>
      ))}
      <form
        className="k-row"
        onSubmit={(e) => {
          e.preventDefault();
          const input = (e.currentTarget.elements.namedItem('v') as HTMLInputElement);
          const v = input.value.trim();
          if (v) { onAdd(v); input.value = ''; }
        }}
      >
        <input name="v" className="k-input" placeholder={placeholder} />
        <button type="submit" className="k-linkbtn" style={{ whiteSpace: 'nowrap' }}>{addLabel}</button>
      </form>
    </div>
  );
}

export function KeyValueRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="k-kv__row">
      <span className="k-kv__label">{label}</span>
      <span className="k-kv__value">{children}</span>
    </div>
  );
}

/** Ícone "i" com balão explicativo (hover/foco). Usado onde um número é estimativa, ex.: interações da cota. */
export function InfoTip({ text, label = 'i' }: { text: ReactNode; label?: string }) {
  return (
    <span className="k-infotip" tabIndex={0} role="note" aria-label={typeof text === 'string' ? text : undefined}>
      <span className="k-infotip__icon" aria-hidden>{label}</span>
      <span className="k-infotip__bubble">{text}</span>
    </span>
  );
}
